/** QL-281 — One goal card in the Goal List: type icon, name, platform, edit/delete. */

import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InlineSvg } from "@/components/ui/inline-svg";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./goal-list-row.i18n";
import type { GoalListGoal } from "./types";

type GoalListRowProps = {
  goal: GoalListGoal;
  /** The goal type's SVG icon, when its collection type has one. */
  iconSvg: string | null;
  platformLabel: string;
  disabled: boolean;
  canDelete: boolean;
  isPending: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

export function GoalListRow({
  goal,
  iconSvg,
  platformLabel,
  disabled,
  canDelete,
  isPending,
  onEdit,
  onDelete,
}: GoalListRowProps) {
  const t = useT(T);
  return (
    <Card className="p-4" data-testid="goal-list-row">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {iconSvg && (
            <InlineSvg svg={iconSvg} className="h-8 w-8 shrink-0 [&_svg]:h-full [&_svg]:w-full" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{goal.name}</div>
            {platformLabel && (
              <p className="text-sm text-muted-foreground truncate">{platformLabel}</p>
            )}
          </div>
        </div>
        {!disabled && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              tooltip={t.edit}
              onClick={onEdit}
              disabled={isPending}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              tooltip={canDelete ? t.delete : t.cannotDeleteLastGoal}
              onClick={onDelete}
              disabled={isPending || !canDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
